/** Shopping cart — listing lines stored locally (web + mobile). */

export const CART_STORAGE_KEY = 'redevicer_cart_v1'

export type CartLine = {
  listing_id: number
  owner_id: number
}

export function isValidCartLine(line: unknown): line is CartLine {
  if (!line || typeof line !== 'object') return false
  const listing_id = Number((line as CartLine).listing_id)
  const owner_id = Number((line as CartLine).owner_id)
  return (
    Number.isFinite(listing_id) &&
    listing_id > 0 &&
    Number.isFinite(owner_id) &&
    owner_id > 0
  )
}

/** Coerce API / form values before persisting (owner_id must survive JSON round-trip). */
export function normalizeCartLine(line: Partial<CartLine>): CartLine | null {
  const listing_id = Number(line.listing_id)
  const owner_id = Number(line.owner_id)
  if (!Number.isFinite(listing_id) || listing_id <= 0) return null
  if (!Number.isFinite(owner_id) || owner_id <= 0) return null
  return { listing_id, owner_id }
}

export function parseCartLines(raw: string | null | undefined): CartLine[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const out: CartLine[] = []
    for (const row of parsed) {
      const norm = normalizeCartLine(row as Partial<CartLine>)
      if (norm) out.push(norm)
    }
    return out
  } catch {
    return []
  }
}

export function serializeCartLines(lines: CartLine[]): string {
  return JSON.stringify(lines)
}

export function cartListingIds(lines: CartLine[]): number[] {
  return lines.map((l) => l.listing_id)
}

export type CartSellerGroup = {
  owner_id: number
  lines: CartLine[]
}

export function groupCartLinesBySeller(lines: CartLine[]): CartSellerGroup[] {
  const map = new Map<number, CartLine[]>()
  for (const line of lines) {
    const bucket = map.get(line.owner_id)
    if (bucket) bucket.push(line)
    else map.set(line.owner_id, [line])
  }
  return Array.from(map.entries()).map(([owner_id, groupLines]) => ({
    owner_id,
    lines: groupLines,
  }))
}

export function addCartLine(lines: CartLine[], line: CartLine): CartLine[] | 'invalid' {
  const norm = normalizeCartLine(line)
  if (!norm) return 'invalid'
  if (lines.some((l) => l.listing_id === norm.listing_id)) {
    return lines
  }
  return [...lines, norm]
}

export function removeCartLine(lines: CartLine[], listingId: number): CartLine[] {
  return lines.filter((l) => l.listing_id !== listingId)
}

export function clearCartLines(): CartLine[] {
  return []
}
